import { useState } from 'react';
import type { FormEvent } from 'react';

import { ApiError, api } from '../api/client';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { useSession } from '../lib/SessionContext';
import { useToast } from './Toast';

/**
 * giveaway: a visitor types the 8-character code printed
 * on a promo card and gets credit. The amount is deliberately not shown
 * anywhere until this succeeds — see llmr-control-api's own comment on why
 * the surprise is the point.
 *
 * Lives on the Add credit page rather than its own route: nobody is
 * expected to bookmark this, and it needs the same balance-refresh plumbing
 * (useSession) the top-up flow already has right there.
 */
export function PromoCodeRedeem() {
  const { dict } = useLanguage();
  const { refreshBalance } = useSession();
  const { showToast } = useToast();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.redeemPromoCode(trimmed);
      setCode('');
      showToast(dict.promoCode.successToast(res.amount.amount));
      await refreshBalance();
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.is('/problems/not-found')) setError(dict.promoCode.errorNotFound);
        else if (e.is('/problems/promo-code-used')) setError(dict.promoCode.errorUsed);
        else if (e.is('/problems/promo-code-expired')) setError(dict.promoCode.errorExpired);
        else if (e.is('/problems/promo-daily-cap-hit')) setError(dict.promoCode.errorCapHit);
        else setError(dict.promoCode.errorGeneric);
      } else {
        setError(dict.promoCode.errorGeneric);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="panel billing-panel">
      <h2>{dict.promoCode.title}</h2>
      <p className="note">{dict.promoCode.description}</p>
      <form onSubmit={submit} className="inline-form">
        <div className="inline-form-field">
          <label htmlFor="promo-code">{dict.promoCode.placeholder}</label>
          <input
            id="promo-code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              setError(null);
            }}
            placeholder={dict.promoCode.placeholder}
            maxLength={8}
            autoComplete="off"
            autoCapitalize="characters"
          />
        </div>
        <button type="submit" disabled={submitting || !code.trim()}>
          {submitting ? dict.promoCode.submitting : dict.promoCode.submit}
        </button>
      </form>
      {error && <p className="field-error">{error}</p>}
    </section>
  );
}
